import { useEffect, useState } from 'react';
import { DialogTitle } from '@headlessui/react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import Modal from '../../../widgets/Modal';
import Button from '../../../widgets/Button';
import { MaxApiLimit } from '../../../../shared/constants/MFA';
import { useMfaModalContext } from '../../../../shared/providers/MfaModalProvider';
import { resendNotification } from '../../../../shared/service/auth';

import notificationScreenJa from '../../../../assets/icons/mfa/notificationScreenEn.png';
import notificationScreenEn from '../../../../assets/icons/mfa/notificationScreenJa.png';

function NotificationSent({ enabled }: { enabled: boolean }) {
  const [countdown, setCountdown] = useState(30);
  const [completed, setCompleted] = useState(false);
  const [maxNotification, setMaxNotification] = useState(false);

  const {
    token,
    needHelpModal,
    setNeedHelpModal,
    stateToken,
    setLogoutModal,
    setPullingInterval,
  } = useMfaModalContext();
  const { t } = useTranslation();

  useEffect(() => {
    if (countdown === 0) {
      setCompleted(true);
    } else {
      const interval = setInterval(() => {
        if (countdown > 0) {
          setCountdown(countdown - 1);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [countdown]);

  const resendNotificationArgs = {
    token: token,
    stateToken: stateToken,
  };

  const { mutate: refetchResendNotification } = useMutation({
    mutationKey: ['resendNotification'],
    mutationFn: () => resendNotification(resendNotificationArgs),
    onSuccess: (res) => {
      setCountdown(30);
      setCompleted(false);
    },
    onError: (err: any) => {
      if (err?.data?.override?.code == MaxApiLimit.MAX_API_LIMIT)
        setMaxNotification(true);
    },
  });

  const handleClick = () => refetchResendNotification();

  function renderResendNotification(
    maxNotification: boolean,
    countdown: number,
    t: any,
  ) {
    if (maxNotification) {
      setPullingInterval(false);
      return t('TRY_AGAIN_LATER');
    } else if (countdown !== 0) {
      return `${t('RESEND_NOTIFICATION')} (${countdown} ${t(
        'SECONDS_COUNTDOWN',
      )})`;
    } else {
      return t('RESEND_NOTIFICATION');
    }
  }

  const isProduction = import.meta.env.PROD;
  const basePath = isProduction
    ? 'assets/'
    : '../../../../src/assets/icons/mfa/';
  const notificationImageName = isProduction
    ? t('NOTIFICATION_PREVIEW_PROD')
    : t('NOTIFICATION_PREVIEW');
  const imagePath = `${basePath.replace(
    '/d',
    '',
  )}assets/${notificationImageName}`;

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  const baseUrl = `${url.protocol}//${url.host}`;

  const finalPath = baseUrl + '/' + basePath + notificationImageName + '.png';

  return (
    <Modal
      open={enabled}
      closeButton
      handleClose={() => {
        !needHelpModal && setLogoutModal(true);
      }}
      modalClassName="w-full max-w-[500px]"
    >
      <div className="text-center">
        <DialogTitle
          as="h4"
          className="text-black-900 mb-2 text-left text-lg font-semibold"
        >
          {t('HRDB_BIOMETRIC_VERIFY_HEADER')}
        </DialogTitle>
        <p className="text-black-600 mb-[10px] text-left text-xs">
          {t('HRDB_BIOMETRIC_VERIFY_SUBHEADER')}
        </p>
        <div className="mt-10 px-6">
          <img src={finalPath} alt="" />
          <img src={notificationScreenEn} className="hidden" alt="" />
          <img src={notificationScreenJa} className="hidden" alt="" />
          <ol className="mt-7 list-decimal space-y-4 pl-5 text-left">
            <li>{t('VERIFY_STEP1')}</li>
            <li>{t('VERIFY_STEP2')}</li>
          </ol>
        </div>

        <div className="mt-[100px] mb-6 flex items-center justify-center gap-x-2">
          <span className="text-black-600 text-right text-xs">
            {t('NO_NOTIFICATION')}
          </span>

          <span
            className="text-primary-600 cursor-pointer text-[13px] font-semibold"
            onClick={() => setNeedHelpModal(true)}
          >
            {t('CLICK_HERE')}
          </span>
        </div>
        <Button
          type="submit"
          variant="outline"
          size="lg"
          className={``}
          disabled={!completed || maxNotification}
          onClick={handleClick}
        >
          <span>{renderResendNotification(maxNotification, countdown, t)}</span>
        </Button>
      </div>
    </Modal>
  );
}

export default NotificationSent;
